// SPDX-License-Identifier: AGPL-3.0-or-later

import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {getAvatarStatusLayout} from '@app/features/ui/components/AvatarStatusLayout';
import type {CSSProperties} from 'react';

export const AVATAR_DECORATION_SIZE_RATIO = 1.3;

export function getAvatarDecorationSizePx(avatarSize: number): number {
	return Math.round(avatarSize * AVATAR_DECORATION_SIZE_RATIO);
}

/** How far the decoration extends past each avatar edge (px). */
export function getAvatarDecorationOverhangPx(avatarSize: number): number {
	return Math.max(0, (getAvatarDecorationSizePx(avatarSize) - avatarSize) / 2);
}

export function getAvatarDecorationStatusCutoutStyle(params: {
	avatarSize: number;
	isMobile?: boolean;
	isTyping?: boolean;
}): CSSProperties | undefined {
	const {avatarSize, isMobile = false, isTyping = false} = params;
	const layout = getAvatarStatusLayout(avatarSize, isMobile);
	if (!layout.supportsStatus) {
		return undefined;
	}
	const decorationSize = getAvatarDecorationSizePx(avatarSize);
	const offset = (decorationSize - avatarSize) / 2;
	const extraGap = Math.max(1.5, layout.borderSize * 0.4);
	const centerX = (isTyping ? layout.typingCutoutCx : layout.cutoutCx) + offset;
	const centerY = (isTyping ? layout.typingCutoutCy : layout.cutoutCy) + offset;
	const radiusX = (isTyping ? layout.typingCutoutWidth / 2 : layout.cutoutRadius) + extraGap;
	const radiusY =
		(isTyping ? layout.typingCutoutHeight / 2 : isMobile ? layout.statusHeight / 2 : layout.cutoutRadius) + extraGap;
	const position = `${remFromPx(centerX)} ${remFromPx(centerY)}`;
	const size = `${remFromPx(radiusX)} ${remFromPx(radiusY)}`;
	const mask = `radial-gradient(ellipse ${size} at ${position}, transparent 99%, #000 100%)`;
	return {
		WebkitMaskImage: mask,
		maskImage: mask,
		WebkitMaskRepeat: 'no-repeat',
		maskRepeat: 'no-repeat',
		WebkitMaskSize: '100% 100%',
		maskSize: '100% 100%',
	};
}
